<template>
  <div id="extrusion-furnaces-container" class="operator-page">
    <!-- Operator Sub-Header / Tabs -->
    <div class="furnaces-top-header d-flex align-items-center justify-content-between">
      <div class="tabs-bar">
        <div class="tab-item active">
          <span>Fornos</span>
        </div>
      </div>
      <div class="header-actions pr-2">
        <button
          type="button"
          class="btn-history d-flex align-items-center"
          @click="openHistoryModal"
        >
          <feather-icon icon="ClockIcon" size="16" class="mr-50" />
          <span>Histórico de Fornadas</span>
        </button>
      </div>
    </div>

    <div class="operator-body">
      <!-- Back Link -->
      <div class="back-link-wrapper mb-2">
        <button class="btn-back-link" @click="goBack">
          <feather-icon icon="ChevronLeftIcon" size="18" class="mr-25" />
          <span>Voltar ao menu</span>
        </button>
      </div>

      <!-- Section Title & Description -->
      <div class="section-heading mb-3">
        <h3 class="heading-title mb-50">Selecione o forno</h3>
        <p class="heading-subtitle mb-0">
          Passe o mouse sobre um forno para visualizar a carga atual e o tempo de operação.
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="loading && displayFurnaces.length === 0" class="loading-state text-center py-5">
        <b-spinner variant="warning" label="Carregando..." class="mb-1" />
        <p class="loading-text">Carregando status dos fornos...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="!loading && displayFurnaces.length === 0" class="empty-furnaces-state text-center py-5">
        <feather-icon icon="InboxIcon" size="48" class="text-muted mb-1" />
        <h4 class="text-muted font-weight-bold">Nenhum forno encontrado</h4>
        <p class="text-muted mb-0">Nenhum forno ativo disponível para o seu centro de trabalho nesta unidade (SiteId: {{ currentSiteId }}).</p>
      </div>

      <!-- Fornos Grid Container -->
      <div v-else class="furnaces-grid-container">
        <div
          v-for="furnace in displayFurnaces"
          :key="furnace.furnaceId"
          class="furnace-card-wrapper"
          @mouseenter="onCardMouseEnter(furnace.furnaceId)"
          @mouseleave="onCardMouseLeave"
        >
          <!-- Card do Forno -->
          <div
            class="furnace-card-box"
            :class="getFurnaceCardClass(furnace)"
            @click="handleFurnaceClick(furnace)"
          >
            <!-- Top do Card: Ícone Squircle à esquerda e Badge à direita -->
            <div class="card-top-row d-flex align-items-center justify-content-between">
              <!-- Ícone Squircle -->
              <div
                class="furnace-squircle-icon"
                :class="furnace.status === 'Disponível' ? 'icon-idle' : 'icon-active'"
              >
                <feather-icon icon="ZapIcon" size="22" />
              </div>

              <!-- Badge de Status -->
              <div
                class="furnace-status-pill"
                :class="getStatusPillClass(furnace.status)"
              >
                {{ furnace.status }}
              </div>
            </div>

            <!-- Centro do Card: Nome e Descrição da Carga -->
            <div class="card-middle-content">
              <div class="d-flex align-items-center mb-25 flex-wrap">
                <h4 class="furnace-box-title mb-0 mr-50">{{ furnace.furnaceName }}</h4>
                <span v-if="furnace.furnaceCode" class="badge badge-light-secondary font-weight-bold font-size-xs">
                  {{ furnace.furnaceCode }}
                </span>
              </div>
              <p v-if="furnace.workcenterName && furnace.workcenterName !== 'Fornos'" class="text-muted small mb-25 font-weight-600">
                {{ furnace.workcenterName }}
              </p>
              <p class="furnace-box-subtitle mb-0">
                <template v-if="furnace.loadedBasketsCount > 0">
                  {{ furnace.loadedBasketsCount }} cestos na carga atual
                </template>
                <template v-else>
                  Sem carga em processamento
                </template>
              </p>
            </div>

            <!-- Rodapé do Card: Ação com Seta -->
            <div class="card-bottom-action d-flex align-items-center justify-content-between">
              <span class="action-link-text">
                {{ furnace.status === 'Disponível' ? 'Abrir montagem de carga' : 'Ver detalhes e editar' }}
              </span>
              <feather-icon icon="ChevronRightIcon" size="18" class="action-arrow-icon" />
            </div>
          </div>

          <!-- POPOVER FLUTUANTE (Exibido apenas no hover quando houver carga) -->
          <transition name="fade-slide">
            <div
              v-if="hoveredFurnaceId === furnace.furnaceId && furnace.currentCharge"
              class="furnace-floating-popover"
            >
              <div class="popover-header-custom d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <feather-icon icon="ZapIcon" size="15" class="mr-50 text-warning" />
                  <strong class="text-dark">{{ furnace.furnaceName }} — Carga Atual</strong>
                </div>
                <span class="badge badge-light-warning font-weight-bold">
                  {{ furnace.loadedBasketsCount }} / {{ furnace.capacity || 12 }} Cestos
                </span>
              </div>

              <div class="popover-body-custom">
                <div class="popover-meta d-flex align-items-center justify-content-between mb-1 pb-50 border-bottom">
                  <div>
                    <small class="text-muted d-block font-weight-600">Código da Carga</small>
                    <strong class="text-dark font-size-sm">{{ furnace.currentCharge.chargeCode }}</strong>
                  </div>
                  <div class="text-right">
                    <small class="text-muted d-block font-weight-600">Tempo de Operação</small>
                    <strong class="text-success font-size-sm font-weight-bold">{{ getLiveTimer(furnace.furnaceId) }}</strong>
                  </div>
                </div>

                <!-- Lista de Cestos no Hover -->
                <div
                  v-if="furnace.currentCharge.baskets && furnace.currentCharge.baskets.length > 0"
                  class="popover-baskets-list"
                >
                  <div
                    v-for="(group, bIdx) in getGroupedChargeBaskets(furnace.currentCharge.baskets)"
                    :key="group.key"
                    class="popover-basket-item d-flex align-items-center justify-content-between"
                  >
                    <div class="d-flex align-items-center mr-1">
                      <span class="popover-idx mr-50">{{ bIdx + 1 }}</span>
                      <div>
                        <div class="d-flex align-items-center">
                          <strong class="text-warning mr-50">{{ group.basketCode }}</strong>
                          <span v-if="group.items.length > 1" class="badge badge-light-primary font-size-xs mr-50">
                            {{ group.items.length }} OPs
                          </span>
                          <span v-else-if="group.items[0] && group.items[0].orderNumber" class="popover-op-badge">
                            OP {{ group.items[0].orderNumber }}
                          </span>
                        </div>
                        <div v-if="group.items.length === 1" class="popover-mat-text" :title="group.items[0].materialName">
                          {{ group.items[0].materialName || 'Material Padrão' }}
                          <span v-if="group.items[0].temper" class="badge badge-light-secondary font-size-xs ml-25 font-weight-600">
                            {{ group.items[0].temper }}
                          </span>
                        </div>
                        <div v-else class="popover-mat-text text-muted small">
                          {{ group.items.map(i => (i.orderNumber ? `OP ${i.orderNumber}` : '')).filter(Boolean).join(', ') }}
                        </div>
                      </div>
                    </div>

                    <div class="text-right whitespace-nowrap">
                      <strong class="text-dark">{{ group.totalBarQuantity }}</strong>
                      <small class="text-muted"> un</small>
                      <div class="popover-size-text">
                        {{ group.items[0] && group.items[0].barSizeMm ? formatBarSize(group.items[0].barSizeMm) : '-' }}
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="text-muted small text-center py-1">
                  Nenhum cesto detalhado nesta carga.
                </div>

                <div class="popover-footer-action mt-1 pt-50 border-top d-flex justify-content-end">
                  <button
                    class="btn btn-sm btn-outline-warning py-25 px-50 font-size-xs d-flex align-items-center"
                    @click.stop="openAssemblyModal(furnace)"
                  >
                    <feather-icon icon="Edit3Icon" size="12" class="mr-25" />
                    <span>Editar Cestos</span>
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes / Acompanhamento da Carga -->
    <FurnaceChargeDetailsModal
      :open.sync="detailsModalOpen"
      :furnace="selectedFurnace"
      :charge-data="selectedFurnace ? selectedFurnace.currentCharge : null"
      :site-id="currentSiteId"
      @charge-updated="fetchFurnaces"
      @charge-finalized="fetchFurnaces"
    />

    <!-- Modal de Histórico de Fornadas -->
    <FurnaceHistoryModal
      :open.sync="historyModalOpen"
      :furnaces="furnaces"
      :site-id="currentSiteId"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { BSpinner } from 'bootstrap-vue';
import { getFurnaces } from './extrusionApi';
import FurnaceChargeDetailsModal from './components/FurnaceChargeDetailsModal.vue';
import FurnaceHistoryModal from './components/FurnaceHistoryModal.vue';

export default {
  name: 'ExtrusionFurnaces',
  components: {
    BSpinner,
    FurnaceChargeDetailsModal,
    FurnaceHistoryModal
  },
  data() {
    return {
      furnaces: [],
      loading: false,
      selectedFurnace: null,
      assemblyModalOpen: false,
      detailsModalOpen: false,
      historyModalOpen: false,
      hoveredFurnaceId: null,
      hoverTimeout: null,
      timerInterval: null,
      autoRefreshInterval: null,
      liveTimers: {}
    };
  },
  computed: {
    currentSiteId() {
      const cookieSite = this.$cookies?.get('userInfo')?.currentSite;
      if (cookieSite) {
        if (typeof cookieSite === 'object' && cookieSite.id) return cookieSite.id;
        if (typeof cookieSite === 'number') return cookieSite;
        if (!isNaN(cookieSite)) return parseInt(cookieSite, 10);
      }
      const storedSite = localStorage.getItem('currentSite') || localStorage.getItem('siteId');
      if (storedSite) {
        try {
          const parsed = JSON.parse(storedSite);
          if (parsed && parsed.id) return parsed.id;
          if (typeof parsed === 'number') return parsed;
        } catch (e) {
          if (!isNaN(storedSite)) return parseInt(storedSite, 10);
        }
      }
      const authUser = this.$store ? this.$store.state?.auth?.user : null;
      if (authUser && authUser.siteId) return authUser.siteId;
      return 1;
    },
    displayFurnaces() {
      return this.furnaces || [];
    }
  },
  mounted() {
    this.fetchFurnaces();
    this.startLiveTimers();
    // Auto-refresh data every 30s
    this.autoRefreshInterval = setInterval(() => {
      this.fetchFurnaces(false);
    }, 30000);
  },
  beforeDestroy() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.autoRefreshInterval) clearInterval(this.autoRefreshInterval);
    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
  },
  methods: {
    async fetchFurnaces(showSpinner = true) {
      if (showSpinner) this.loading = true;
      try {
        const res = await getFurnaces(this.currentSiteId);
        const list = res.data && (Array.isArray(res.data.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []));
        this.furnaces = list;
        this.updateTimersFromData();
      } catch (err) {
        console.error('Erro ao buscar status dos fornos:', err);
        this.furnaces = [];
      } finally {
        if (showSpinner) this.loading = false;
      }
    },
    getGroupedChargeBaskets(baskets) {
      if (!Array.isArray(baskets)) return [];
      const map = new Map();
      for (const b of baskets) {
        const rawCode = b.basketCode ? String(b.basketCode).trim() : '';
        const key = rawCode ? rawCode.toUpperCase() : `ID_${b.id}`;
        if (!map.has(key)) {
          map.set(key, {
            key,
            basketCode: rawCode || (b.id ? `C${b.id}` : '-'),
            items: [],
            totalBarQuantity: 0
          });
        }
        const group = map.get(key);
        group.items.push(b);
        group.totalBarQuantity += Number(b.barQuantity || 0);
      }
      return Array.from(map.values());
    },
    onCardMouseEnter(furnaceId) {
      if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
      this.hoveredFurnaceId = furnaceId;
    },
    onCardMouseLeave() {
      this.hoverTimeout = setTimeout(() => {
        this.hoveredFurnaceId = null;
      }, 120);
    },
    startLiveTimers() {
      if (this.timerInterval) clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => {
        this.displayFurnaces.forEach(f => {
          if (f.currentCharge && f.currentCharge.startedAt && f.status !== 'Disponível') {
            const startMs = moment(f.currentCharge.startedAt).valueOf();
            const totalSecs = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
            this.$set(this.liveTimers, f.furnaceId, this.formatDuration(totalSecs));
          } else {
            this.$set(this.liveTimers, f.furnaceId, '00:00:00');
          }
        });
      }, 1000);
    },
    updateTimersFromData() {
      this.displayFurnaces.forEach(f => {
        if (f.currentCharge && f.currentCharge.startedAt && f.status !== 'Disponível') {
          const startMs = moment(f.currentCharge.startedAt).valueOf();
          const totalSecs = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
          this.$set(this.liveTimers, f.furnaceId, this.formatDuration(totalSecs));
        } else {
          this.$set(this.liveTimers, f.furnaceId, '00:00:00');
        }
      });
    },
    getLiveTimer(furnaceId) {
      return this.liveTimers[furnaceId] || '00:00:00';
    },
    formatDuration(totalSecs) {
      if (!totalSecs || totalSecs < 0) return '00:00:00';
      const hrs = Math.floor(totalSecs / 3600);
      const mins = Math.floor((totalSecs % 3600) / 60);
      const secs = totalSecs % 60;
      return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    },
    formatBarSize(size) {
      if (!size) return '-';
      let num = Number(size);
      if (isNaN(num) || num <= 0) return '-';
      if (num <= 50) num *= 1000;
      return `${num.toFixed(0)} mm`;
    },
    getStatusPillClass(status) {
      if (status === 'Em operação') return 'pill-operating';
      if (status === 'Aquecendo') return 'pill-heating';
      return 'pill-available';
    },
    getFurnaceCardClass(furnace) {
      if (furnace.status === 'Em operação') return 'card-is-operating';
      if (furnace.status === 'Aquecendo') return 'card-is-heating';
      return 'card-is-available';
    },
    handleFurnaceClick(furnace) {
      if (furnace.status === 'Disponível') {
        this.openAssemblyModal(furnace);
      } else {
        this.openDetailsModal(furnace);
      }
    },
    openAssemblyModal(furnace) {
      const prefix = this.$route.params.prefix || 'sfc';
      this.$router.push({
        name: 'operator-extrusion-furnace-assembly',
        params: { prefix, furnaceId: furnace.furnaceId }
      });
    },
    openDetailsModal(furnace) {
      this.selectedFurnace = furnace;
      this.detailsModalOpen = true;
    },
    openHistoryModal() {
      this.historyModalOpen = true;
    },
    handleChargeCreated() {
      this.fetchFurnaces();
    },
    goBack() {
      const prefix = this.$route.params.prefix || 'sfc';
      this.$router.push({ name: 'operator-extrusion-resources', params: { prefix } });
    }
  }
};
</script>

<style lang="scss">
.app-content-operator:has(#extrusion-furnaces-container) {
  padding: 0 !important;
}

#extrusion-furnaces-container {
  min-height: calc(100vh - 110px);
  background-color: #fbfaf9;
  color: #332f2e;

  /* Top Tab Bar */
  .furnaces-top-header {
    background: #fff;
    border-bottom: 1px solid #ebe6e2;

    .tabs-bar {
      display: flex;
      padding-left: 28px;

      .tab-item {
        padding: 16px 28px;
        font-family: 'Raleway', sans-serif;
        font-size: 16px;
        font-weight: 700;
        color: #332f2e;
        cursor: pointer;
        position: relative;

        &.active {
          color: #332f2e;
          border-top: 3px solid #e86b24;
          border-left: 1px solid #ebe6e2;
          border-right: 1px solid #ebe6e2;
          background: #fff;
          margin-bottom: -1px;
        }
      }
    }

    .btn-history {
      background: #ffffff;
      border: 1px solid #974900;
      color: #974900;
      border-radius: 6px;
      font-family: 'Raleway', sans-serif;
      font-size: 13px;
      font-weight: 600;
      padding: 7px 16px;
      display: inline-flex;
      align-items: center;
      cursor: pointer;
      transition: all 0.2s ease-in-out;
      outline: none;

      .feather {
        color: #974900;
      }

      &:hover {
        background: #ffede2;
        border-color: #974900;
        color: #974900;
      }

      &:active,
      &:focus {
        background: #ffdbc4;
        border-color: #7a3b00;
        color: #7a3b00;
        outline: none;
        box-shadow: 0 0 0 2px rgba(151, 73, 0, 0.18);
      }
    }
  }

  .operator-body {
    padding: 24px 32px;
  }

  /* Back Link */
  .btn-back-link {
    background: transparent;
    border: none;
    padding: 0;
    color: #4c4541;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: color 0.2s;

    &:hover {
      color: #974900;
    }
  }

  /* Section Heading */
  .section-heading {
    .heading-title {
      font-family: 'Raleway', sans-serif;
      font-size: 22px;
      font-weight: 700;
      color: #332f2e;
    }

    .heading-subtitle {
      font-size: 13px;
      color: #8b939e;
    }
  }

  /* 3 Columns Grid Container */
  .furnaces-grid-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;

    @media (max-width: 991px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 576px) {
      grid-template-columns: 1fr;
    }

    .furnace-card-wrapper {
      position: relative;
    }

    .furnace-card-box {
      background: #ffffff;
      border: 1px solid #e6e4e2;
      border-radius: 16px;
      padding: 24px;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 200px;
      cursor: pointer;
      position: relative;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
      transition: all 0.25s ease;

      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
        border-color: #d6d0cb;
      }

      /* Card Top Row */
      .card-top-row {
        margin-bottom: 20px;

        .furnace-squircle-icon {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;

          &.icon-active {
            background-color: #e86b24;
          }

          &.icon-idle {
            background-color: #8b939e;
          }
        }

        .furnace-status-pill {
          font-size: 12px;
          font-weight: 600;
          padding: 4px 14px;
          border-radius: 14px;

          &.pill-operating {
            background-color: #fdf0e6;
            color: #e26723;
          }

          &.pill-heating {
            background-color: #fdf0e6;
            color: #e26723;
          }

          &.pill-available {
            background-color: #eaf8f0;
            color: #28c76f;
          }
        }
      }

      /* Middle Content */
      .card-middle-content {
        margin-bottom: 24px;

        .furnace-box-title {
          font-family: 'Raleway', sans-serif;
          font-size: 20px;
          font-weight: 700;
          color: #332f2e;
        }

        .furnace-box-subtitle {
          font-size: 13px;
          color: #8b939e;
        }
      }

      /* Bottom Action Link */
      .card-bottom-action {
        margin-top: auto;
        padding-top: 12px;

        .action-link-text {
          font-family: 'Raleway', sans-serif;
          font-size: 13px;
          font-weight: 700;
          color: #974900;
          transition: color 0.2s;
        }

        .action-arrow-icon {
          color: #974900;
          transition: transform 0.2s;
        }

        &:hover {
          .action-link-text {
            color: #7a3b00;
          }
          .action-arrow-icon {
            transform: translateX(3px);
          }
        }
      }
    }
  }

  /* Floating Popover on Hover */
  .furnace-floating-popover {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    width: 100%;
    min-width: 340px;
    background: #ffffff;
    border: 1px solid #e6e4e2;
    border-radius: 12px;
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.14);
    z-index: 1050;
    overflow: hidden;
    pointer-events: auto;

    .popover-header-custom {
      background: #faf9f8;
      border-bottom: 1px solid #ebe6e2;
      padding: 10px 14px;
      font-size: 13px;
    }

    .popover-body-custom {
      padding: 12px 14px;

      .font-size-sm {
        font-size: 12px;
      }

      .popover-baskets-list {
        max-height: 220px;
        overflow-y: auto;

        .popover-basket-item {
          padding: 6px 0;
          border-bottom: 1px solid #f5f3f1;

          &:last-child {
            border-bottom: none;
          }

          .popover-idx {
            width: 18px;
            height: 18px;
            background: #f0effc;
            color: #7367f0;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            font-weight: 700;
          }

          .popover-op-badge {
            font-size: 10px;
            font-weight: 600;
            background: #f0effc;
            color: #7367f0;
            padding: 1px 5px;
            border-radius: 3px;
          }

          .popover-mat-text {
            font-size: 11px;
            color: #6e6b7b;
            max-width: 170px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .popover-size-text {
            font-size: 10px;
            color: #8b939e;
          }
        }
      }
    }
  }

  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .fade-slide-enter,
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }
}
</style>
